import { format, isValid } from 'date-fns';
import type { SocialActionLogRow } from './types';

/**
 * Rendering a log row for a person.
 *
 * Every timestamp is drawn in the BROWSER's zone, unlabelled — the same as every other date in the
 * product. Naming the zone was a standing note in the reader's way, answering a question they had
 * already answered by being in it.
 */

/** Shown when a nullable timestamp, handle or score simply is not there. */
export const NOT_RECORDED = '—';

const toDate = (iso: string | null | undefined): Date | null => {
	if (!iso) return null;
	const parsed = new Date(iso);
	return isValid(parsed) ? parsed : null;
};

/** Day only, e.g. `19 Aug 2026`. */
export const formatLogDate = (iso: string | null | undefined): string => {
	const date = toDate(iso);
	return date ? format(date, 'd MMM yyyy') : NOT_RECORDED;
};

/**
 * Time of day to the SECOND.
 *
 * Not a nicety: the engine writes one row per rule-decision and several rules can resolve inside
 * the same second, so minute precision would present distinct decisions as simultaneous and make
 * the order they are listed in look arbitrary.
 */
export const formatLogTime = (iso: string | null | undefined): string => {
	const date = toDate(iso);
	return date ? format(date, 'HH:mm:ss') : NOT_RECORDED;
};

/** Both halves, for tooltips and the export dialog where a single string is wanted. */
export const formatLogTimestamp = (iso: string | null | undefined): string => {
	const date = toDate(iso);
	return date ? `${formatLogDate(iso)}, ${formatLogTime(iso)}` : NOT_RECORDED;
};

// ── confidence ────────────────────────────────────────────────────────────────

/**
 * Is this a score we can render?
 *
 * Guards every confidence path so `null`, `undefined` and `NaN` all take the same branch. A NaN
 * that slipped through would render as `NaN%` rather than as the em dash that means not recorded.
 */
export const isScore = (value: number | null | undefined): value is number =>
	typeof value === 'number' && Number.isFinite(value);

/**
 * A 0–1 score as a percentage.
 *
 * `null` is NOT rendered as 0%. A missing score means the model returned none, which says nothing
 * about the match — showing it as zero would read as a confident failure.
 */
/**
 * A score as a whole percent, except at the two ends where rounding lies.
 *
 * `Math.round` turns 0.004 into "0%" and 0.998 into "100%", and both readings are wrong in a way
 * that matters here: "0%" reads as no measurement at all rather than a confident no, and "100%"
 * claims a certainty the model did not express. A blocked action explained by "0%" sent someone
 * looking for a broken pipeline when the model had simply answered NO.
 */
export const formatConfidence = (value: number | null | undefined): string => {
	if (!isScore(value)) return NOT_RECORDED;
	if (value > 0 && value < 0.005) return '<1%';
	if (value < 1 && value >= 0.995) return '>99%';
	return `${Math.round(value * 100)}%`;
};

/**
 * `92% (gate 95%)`, so the score and the floor it had to clear are never read apart.
 *
 * The floor comes from the ROW — `riskThreshold`, the value the engine actually applied — and
 * there is deliberately no fallback. This module used to mirror the server's threshold table and
 * the copy drifted: it advertised 85% while the engine gated at 95%. An audit log that names a
 * gate nobody applied is worse than one that names none, so a missing threshold shows the bare
 * score instead.
 */
export const formatConfidenceAgainstGate = (
	value: number | null | undefined,
	threshold: number | null | undefined
): string => {
	const score = formatConfidence(value);
	return isScore(threshold) ? `${score} (gate ${formatConfidence(threshold)})` : score;
};

/**
 * How to colour a confidence score — against the gate it was measured against, never in the
 * abstract.
 *
 * There is no such thing as a good score on its own. 78% clears the floor for a close and misses
 * it for a delete, so a fixed high/medium/low band paints the same number green on a row that
 * fired and green again on a row the gate stopped — which is how a blocked 0% came to render as a
 * success.
 *
 * Without a recorded threshold the pill stays neutral. The score is still shown; what we decline
 * to do is imply a verdict we cannot derive, which is the same reason `formatConfidenceAgainstGate`
 * has no fallback floor.
 */
export const confidencePillClasses = (
	value: number | null | undefined,
	threshold: number | null | undefined
): string => {
	if (!isScore(value) || !isScore(threshold)) return 'bg-muted text-muted-foreground';
	return value >= threshold ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700';
};

// ── rule scores ──────────────────────────────────────────────────────────────────

/** One rule (or rules sharing a condition) and the probability Jev gave it. */
export interface RuleScore {
	/** Rule names, joined — rules with an identical condition share one score. */
	label: string;
	probability: number;
	/** The rule this row acted on, or was blocked on. */
	isWinner: boolean;
}

export interface RuleScores {
	scores: RuleScore[];
	/** Jev's probability that no rule applies. Absent when the row does not carry it. */
	noRuleProbability: number | null;
}

const isProbability = (value: unknown): value is number =>
	typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1;

const toRuleScore = (candidate: unknown, winnerName: string | null): RuleScore | null => {
	if (typeof candidate !== 'object' || candidate === null) return null;
	const { rules, probability } = candidate as { rules?: unknown; probability?: unknown };
	if (!Array.isArray(rules) || !isProbability(probability)) return null;
	const names = rules.filter((name): name is string => typeof name === 'string' && name.trim() !== '');
	if (names.length === 0) return null;
	return { label: names.join(' · '), probability, isWinner: winnerName !== null && names.includes(winnerName) };
};

/** Whether the Jev engine decided this row — its fact sheet says so. */
export const isJevDecision = (row: Pick<SocialActionLogRow, 'factSheet'>): boolean => row.factSheet?.['engine'] === 'jev';

/**
 * How each rule scored, for rows Jev decided.
 *
 * Null for every other row: the LLM engine scores only the winning rule, so there is no ranking to
 * show, and an empty block would suggest the scores were lost. Every value comes from a JSON
 * column, so each is checked — one malformed candidate is dropped rather than rendered as NaN%.
 */
export const ruleScoresOf = (row: Pick<SocialActionLogRow, 'factSheet' | 'ruleName'>): RuleScores | null => {
	const factSheet = row.factSheet;
	if (!factSheet || !isJevDecision(row) || !Array.isArray(factSheet['topCandidates'])) return null;

	const scores = factSheet['topCandidates']
		.map((candidate) => toRuleScore(candidate, row.ruleName))
		.filter((score): score is RuleScore => score !== null);
	if (scores.length === 0) return null;

	const noRule = factSheet['noRuleProbability'];
	return { scores, noRuleProbability: isProbability(noRule) ? noRule : null };
};

// ── file sizes ───────────────────────────────────────────────────────────────────

const BYTES_PER_KB = 1024;
const BYTES_PER_MB = BYTES_PER_KB * 1024;

/**
 * A byte count as a size a person can judge without counting digits.
 *
 * `3355443` and `335544` differ by one character and by a factor of ten, and the reader of an
 * audit row is being asked whether what we removed was a thumbnail or a video — a question the raw
 * count answers only after arithmetic. Binary units, matching the cap the server enforces.
 *
 * Anything that is not a real, non-negative count renders as not-recorded rather than as `NaN B`
 * or as a confident `0 B`: a size we cannot state is not a size of zero.
 */
export const formatByteSize = (bytes: number | null | undefined): string => {
	if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) return NOT_RECORDED;
	if (bytes < BYTES_PER_KB) return `${Math.round(bytes)} B`;
	if (bytes < BYTES_PER_MB) return `${(bytes / BYTES_PER_KB).toFixed(1)} KB`;
	return `${(bytes / BYTES_PER_MB).toFixed(1)} MB`;
};

/** Whatever the row recorded, or the em dash. Keeps the null-handling out of the markup. */
export const orNotRecorded = (value: string | null | undefined): string => {
	const trimmed = value?.trim();
	return trimmed ? trimmed : NOT_RECORDED;
};

/**
 * The "2d" beside a name in the detail panel.
 *
 * Deliberately coarse: the exact timestamp is already a column in the table, and what a reader
 * wants beside a message is how long ago it was, not when. Returns null rather than a placeholder
 * when there is no date — an age we cannot state is better left off than guessed at.
 */
export const relativeAge = (value: string | null | undefined): string | null => {
	if (!value) return null;

	const then = new Date(value).getTime();
	if (Number.isNaN(then)) return null;

	const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
	if (seconds < 60) return 'now';

	const minutes = Math.round(seconds / 60);
	if (minutes < 60) return `${minutes}m`;

	const hours = Math.round(minutes / 60);
	if (hours < 24) return `${hours}h`;

	const days = Math.round(hours / 24);
	return `${days}d`;
};

/**
 * The comment's text, or nothing at all when the picture is the comment.
 *
 * Three states, and the middle one is the reason this is not `orNotRecorded`:
 *
 *  - text          → the text
 *  - no text, media→ `null`, so the caller draws the attachment and nothing beside it. A media-only
 *                    comment is not a comment missing its words; the image IS what was said, and
 *                    labelling it "not recorded" reports a gap in a record that is complete.
 *  - neither       → the em dash, where the emptiness really is a gap.
 */
export const commentTextOrMediaAlone = (
	commentSnapshot: string | null | undefined,
	mediaCount: number
): string | null => {
	const trimmed = commentSnapshot?.trim();
	if (trimmed) return trimmed;
	return mediaCount > 0 ? null : NOT_RECORDED;
};
